import { useMemo, useState } from "react";
import ModalSheet from "./ModalSheet";
import {
  CHIP_TONES, COLOR, FONT,
  pickerKicker, pickerTitle, pickerOptionStyle,
} from "../lib/tokens";

// Multi-step form for authoring a custom recipe. Produces a recipe
// object matching the bundled schema (src/data/recipes/schema.js) so
// it can be dropped straight into CookMode and saved to user_recipes.
//
// Steps (top-to-bottom, not a tab bar — one long scroll):
//   1. Identity  — title + emoji + cuisine + category + difficulty
//   2. Timing    — prep / cook minutes + serves
//   3. Ingredients (amount + item + optional pantry link)
//   4. Steps     — title + instruction rows, add/remove, reorder
//   5. Review    — SAVE & COOK
//
// Kept simple on purpose. Power-users can edit after saving in a
// follow-up ship; for the first version the only exit is Save & Cook
// or Cancel.

const CUISINE_OPTIONS = [
  "italian", "french", "mexican", "american", "japanese",
  "thai", "indian", "chinese", "mediterranean", "other",
];

const CATEGORY_OPTIONS = [
  "pasta", "eggs", "lunch", "soup", "salad", "chicken",
  "beef", "pork", "fish", "vegetarian", "dessert", "sauce",
  "snack", "other",
];

// Meal-composition tags — orthogonal to category. `category` describes
// WHAT the recipe is ("pasta", "soup"); these describe its ROLE on the
// plate ("main", "side") and WHEN it's eaten ("breakfast", "dinner").
// Same enums as AIRecipe's COURSE_CHIPS / MEAL_TIMING_CHIPS and the
// generate-recipe edge function so a recipe saved from either path
// filters identically in the library.
const COURSE_OPTIONS = [
  { id: "any",       label: "— any —"   },
  { id: "main",      label: "main"      },
  { id: "side",      label: "side"      },
  { id: "dessert",   label: "dessert"   },
  { id: "appetizer", label: "appetizer" },
  // Component / pantry-building recipes. Don't belong to a meal slot;
  // used as ingredients in other recipes.
  { id: "bake",      label: "bake (bread, pastry)" },
  { id: "prep",      label: "prep (stock, sauce)"   },
];
const MEAL_TIMING_OPTIONS = [
  { id: "any",       label: "— any —"   },
  { id: "breakfast", label: "breakfast" },
  { id: "lunch",     label: "lunch"     },
  { id: "dinner",    label: "dinner"    },
];

const EMOJI_SUGGESTIONS = [
  "🍝", "🍲", "🥗", "🍳", "🥞", "🍔", "🌮", "🥘", "🍱",
  "🥟", "🍛", "🍜", "🍣", "🍕", "🥪", "🍰", "🥐", "🍖", "🍗",
];

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export default function CustomRecipeBuilder({
  pantry = [],
  onCancel,
  onSave,          // (recipe, opts) => Promise — persist, then close
  onSchedule,      // (recipe, opts) => Promise — parent persists + opens SchedulePicker
  onSaveAndCook,   // (recipe, opts) => Promise — existing save + cook path
}) {
  // Identity
  const [title,      setTitle]      = useState("");
  const [emoji,      setEmoji]      = useState("🍽️");
  const [cuisine,    setCuisine]    = useState("other");
  const [category,   setCategory]   = useState("other");
  const [course,     setCourse]     = useState("any");   // main/side/dessert/appetizer
  const [mealTiming, setMealTiming] = useState("any");   // breakfast/lunch/dinner
  const [difficulty, setDifficulty] = useState(3);  // 1..10

  // Timing
  const [prep,   setPrep]   = useState(10);
  const [cook,   setCook]   = useState(20);
  const [serves, setServes] = useState(2);

  // Ingredients — each row: { amount, item, ingredientId|null }
  // Start with 3 empty rows so the surface doesn't feel empty.
  const [ingredients, setIngredients] = useState([
    { id: uid(), amount: "", item: "", ingredientId: null },
    { id: uid(), amount: "", item: "", ingredientId: null },
    { id: uid(), amount: "", item: "", ingredientId: null },
  ]);

  // Steps — each row: { title, instruction }
  const [steps, setSteps] = useState([
    { id: uid(), title: "", instruction: "" },
    { id: uid(), title: "", instruction: "" },
  ]);

  // Per-action busy state so each button can show SAVING… without
  // racing its siblings.
  const [busy, setBusy] = useState(null);  // null | "save" | "schedule" | "cook"
  const saving = !!busy;
  // Privacy + admin-review opt-ins. Default private (per-user spec).
  // Submitting to admin implies the author wants the recipe considered
  // for promotion to the bundled library — only meaningful for CUSTOM
  // recipes (AI drafts aren't offered the same lane).
  const [shareWithFamily, setShareWithFamily]  = useState(false);
  const [submitForReview, setSubmitForReview]  = useState(false);

  // Pantry-match suggestions — for each ingredient row, show a
  // compact list of pantry items whose name or canonicalId matches
  // the row's item text. Tapping attaches the pantry canonicalId.
  const pantryLookup = useMemo(() => {
    return pantry
      .filter(p => (p.name || p.canonicalId))
      .map(p => ({
        name: p.name || p.canonicalId,
        canonicalId: p.canonicalId || null,
        category: p.category || "",
      }));
  }, [pantry]);

  const isValid = title.trim().length > 0 &&
                  ingredients.some(i => i.item.trim()) &&
                  steps.some(s => s.title.trim() || s.instruction.trim());

  const updateIngredient = (id, patch) => {
    setIngredients(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
  };
  const removeIngredient = (id) => {
    setIngredients(prev => prev.filter(i => i.id !== id));
  };
  const addIngredient = () => {
    setIngredients(prev => [...prev, { id: uid(), amount: "", item: "", ingredientId: null }]);
  };

  const updateStep = (id, patch) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
  };
  const removeStep = (id) => {
    setSteps(prev => prev.filter(s => s.id !== id));
  };
  const addStep = () => {
    setSteps(prev => [...prev, { id: uid(), title: "", instruction: "" }]);
  };

  // Snapshot the form into a schema.js-shaped recipe object. Pure —
  // called by all three action handlers with the same inputs so the
  // persisted row matches no matter which button fires.
  const buildRecipe = () => {
    const slug = slugify(title) || "my-recipe";
    const cleanIngredients = ingredients
      .filter(i => i.item.trim())
      .map(i => ({
        amount:       i.amount.trim() || null,
        item:         i.item.trim(),
        ingredientId: i.ingredientId || null,
      }));
    const cleanSteps = steps
      .filter(s => s.title.trim() || s.instruction.trim())
      .map((s, idx) => ({
        id:          `step${idx + 1}`,
        title:       s.title.trim() || `Step ${idx + 1}`,
        instruction: s.instruction.trim(),
        icon:        "👨‍🍳",
        timer:       null,
        tip:         null,
      }));
    return {
      slug,
      title:      title.trim(),
      subtitle:   null,
      emoji:      emoji || "🍽️",
      cuisine,
      category,
      course:     course === "any" ? null : course,
      mealTiming: mealTiming === "any" ? null : mealTiming,
      difficulty: clamp(difficulty, 1, 10),
      routes:     ["plan"],
      time:       { prep: Number(prep) || 0, cook: Number(cook) || 0 },
      serves:     clamp(serves, 1, 20),
      tools:      [],
      ingredients: cleanIngredients,
      steps:       cleanSteps,
      tags:        [],
    };
  };

  // One handler for all three actions — differs only in the parent
  // callback invoked and the sharing rule (scheduling implies shared=true,
  // everything else respects the toggle).
  const handleAction = (kind) => async () => {
    if (!isValid || busy) return;
    setBusy(kind);
    try {
      const recipe = buildRecipe();
      const opts = {
        shared: kind === "schedule" ? true : shareWithFamily,
        submitForReview,
      };
      if (kind === "save")     await onSave?.(recipe, opts);
      if (kind === "schedule") await onSchedule?.(recipe, opts);
      if (kind === "cook")     await onSaveAndCook?.(recipe, opts);
    } catch (e) {
      console.error(`[custom builder] ${kind} failed:`, e);
    } finally {
      setBusy(null);
    }
  };
  const handleSave     = handleAction("save");
  const handleSchedule = handleAction("schedule");
  const handleCook     = handleAction("cook");

  return (
    <div>
      <div style={{ padding: "24px 20px 0", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onCancel} style={iconBtn}>←</button>
        <div style={{ flex: 1, fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em" }}>
          CUSTOM RECIPE
        </div>
      </div>

      <div style={{ padding: "12px 20px 120px" }}>
        {/* Identity */}
        <Section label="IDENTITY">
          <Field label="TITLE">
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Mom's meatballs"
              style={inputStyle}
            />
          </Field>
          <Field label="EMOJI">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {EMOJI_SUGGESTIONS.map(e => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: emoji === e ? COLOR.goldDeep : COLOR.soil,
                    border: `1px solid ${emoji === e ? COLOR.gold : COLOR.border}`,
                    fontSize: 20, cursor: "pointer",
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          </Field>
          <Row>
            <Field label="CUISINE">
              <SelectButton
                label="CUISINE"
                kickerColor={CHIP_TONES.category.fg}
                title="Pick a cuisine"
                value={cuisine}
                options={CUISINE_OPTIONS}
                onChange={setCuisine}
              />
            </Field>
            <Field label="CATEGORY">
              <SelectButton
                label="CATEGORY"
                kickerColor={CHIP_TONES.category.fg}
                title="Pick a category"
                value={category}
                options={CATEGORY_OPTIONS}
                onChange={setCategory}
              />
            </Field>
          </Row>
          <Row>
            <Field label="COURSE">
              <SelectButton
                label="COURSE"
                kickerColor={CHIP_TONES.category.fg}
                title="Which course is this?"
                value={course}
                options={COURSE_OPTIONS}
                onChange={setCourse}
              />
            </Field>
            <Field label="MEAL TIMING">
              <SelectButton
                label="MEAL TIMING"
                kickerColor={CHIP_TONES.category.fg}
                title="When is this eaten?"
                value={mealTiming}
                options={MEAL_TIMING_OPTIONS}
                onChange={setMealTiming}
              />
            </Field>
          </Row>
          <Field label={`DIFFICULTY · ${difficultyWord(difficulty)}`}>
            <input
              type="range" min={1} max={10} step={1}
              value={difficulty}
              onChange={e => setDifficulty(Number(e.target.value))}
              style={{ width: "100%", accentColor: COLOR.gold }}
            />
          </Field>
        </Section>

        {/* Timing */}
        <Section label="TIMING">
          <Row>
            <Field label="PREP (MIN)">
              <input
                type="number" min={0}
                value={prep}
                onChange={e => setPrep(e.target.value === "" ? "" : Number(e.target.value))}
                style={inputStyle}
              />
            </Field>
            <Field label="COOK (MIN)">
              <input
                type="number" min={0}
                value={cook}
                onChange={e => setCook(e.target.value === "" ? "" : Number(e.target.value))}
                style={inputStyle}
              />
            </Field>
            <Field label="SERVES">
              <input
                type="number" min={1}
                value={serves}
                onChange={e => setServes(e.target.value === "" ? 1 : Number(e.target.value))}
                style={inputStyle}
              />
            </Field>
          </Row>
        </Section>

        {/* Ingredients */}
        <Section label={`INGREDIENTS · ${ingredients.filter(i => i.item.trim()).length}`}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ingredients.map((ing, idx) => (
              <IngredientRow
                key={ing.id}
                index={idx}
                ing={ing}
                pantry={pantryLookup}
                onChange={(patch) => updateIngredient(ing.id, patch)}
                onRemove={ingredients.length > 1 ? () => removeIngredient(ing.id) : null}
              />
            ))}
          </div>
          <button onClick={addIngredient} style={addBtn}>+ ADD INGREDIENT</button>
        </Section>

        {/* Steps */}
        <Section label={`STEPS · ${steps.filter(s => s.title.trim() || s.instruction.trim()).length}`}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {steps.map((step, idx) => (
              <StepRow
                key={step.id}
                index={idx}
                step={step}
                onChange={(patch) => updateStep(step.id, patch)}
                onRemove={steps.length > 1 ? () => removeStep(step.id) : null}
              />
            ))}
          </div>
          <button onClick={addStep} style={addBtn}>+ ADD STEP</button>
        </Section>

        {/* Privacy + admin-review toggles. Recipes default private;
            user opts in to family visibility or to submit for admin
            review. Scheduling auto-overrides privacy (see handleAction). */}
        <div style={{ marginTop: 24 }}>
          <Toggle
            label="Share with family"
            hint="They'll see it in the recipe picker and can schedule it."
            checked={shareWithFamily}
            onChange={setShareWithFamily}
          />
          <Toggle
            label="Submit to admin for template review"
            hint="An admin may promote great recipes into the bundled library."
            checked={submitForReview}
            onChange={setSubmitForReview}
          />
        </div>

        {/* Action bar — CANCEL (narrow) + SAVE + SCHEDULE + SAVE & COOK. */}
        <div style={{ marginTop: 18, display: "flex", gap: 8 }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              width: 52, padding: "14px 0",
              background: COLOR.soil, border: `1px solid ${COLOR.border}`,
              color: COLOR.dim, borderRadius: 12,
              fontFamily: FONT.mono, fontSize: 10, letterSpacing: "0.06em",
              cursor: saving ? "not-allowed" : "pointer", flexShrink: 0,
              opacity: saving ? 0.5 : 1,
            }}
          >
            ✕
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || saving}
            style={{
              flex: 1, padding: "14px 8px",
              background: "transparent", border: `1px solid ${isValid && !saving ? COLOR.border : COLOR.edge}`,
              color: isValid && !saving ? CHIP_TONES.canonical.fg : COLOR.sub,
              borderRadius: 12,
              fontFamily: FONT.mono, fontSize: 10, fontWeight: 600, letterSpacing: "0.06em",
              cursor: isValid && !saving ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            {busy === "save" ? "…" : "SAVE"}
          </button>
          <button
            onClick={handleSchedule}
            disabled={!isValid || saving}
            style={{
              flex: 1, padding: "14px 8px",
              background: "transparent", border: `1px solid ${isValid && !saving ? COLOR.border : COLOR.edge}`,
              color: isValid && !saving ? CHIP_TONES.canonical.fg : COLOR.sub,
              borderRadius: 12,
              fontFamily: FONT.mono, fontSize: 10, fontWeight: 600, letterSpacing: "0.06em",
              cursor: isValid && !saving ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            {busy === "schedule" ? "…" : "📅 SCHED"}
          </button>
          <button
            onClick={handleCook}
            disabled={!isValid || saving}
            style={{
              flex: 2, padding: "14px",
              background: isValid && !saving ? COLOR.gold : COLOR.soil,
              color: isValid && !saving ? COLOR.night : COLOR.sub,
              border: "none", borderRadius: 12,
              fontFamily: FONT.mono, fontSize: 11,
              fontWeight: 600, letterSpacing: "0.08em",
              cursor: isValid && !saving ? "pointer" : "not-allowed",
            }}
          >
            {busy === "cook" ? "SAVING…" : "COOK IT →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Subcomponents

// Compact self-labeled toggle used for the privacy + submit-for-review
// opt-ins below the review step. Emits a plain boolean so the parent
// can feed it straight into save opts.
function Toggle({ label, hint, checked, onChange }) {
  return (
    <label style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "10px 12px", marginBottom: 8,
      background: COLOR.deep, border: `1px solid ${checked ? "#3a3019" : COLOR.edge}`,
      borderRadius: 12, cursor: "pointer",
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{
          width: 16, height: 16, marginTop: 2, accentColor: COLOR.gold,
          cursor: "pointer", flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 11, fontWeight: 600,
          color: checked ? COLOR.gold : COLOR.dim, letterSpacing: "0.04em",
        }}>
          {label}
        </div>
        {hint && (
          <div style={{
            marginTop: 2, fontFamily: FONT.sans, fontSize: 11,
            color: COLOR.muted, lineHeight: 1.4,
          }}>
            {hint}
          </div>
        )}
      </div>
    </label>
  );
}

function IngredientRow({ index, ing, pantry, onChange, onRemove }) {
  const [suggestOpen, setSuggestOpen] = useState(false);
  const matches = useMemo(() => {
    const q = ing.item.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return pantry
      .filter(p => (p.name || "").toLowerCase().includes(q))
      .slice(0, 4);
  }, [ing.item, pantry]);

  const hasLink = !!ing.ingredientId;

  return (
    <div style={{ background: COLOR.ground, border: `1px solid ${COLOR.edge}`, borderRadius: 12, padding: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, width: 18, textAlign: "center" }}>
          {index + 1}.
        </span>
        <input
          value={ing.amount}
          onChange={e => onChange({ amount: e.target.value })}
          placeholder="2 tbsp"
          style={{ ...inputStyle, width: 90 }}
        />
        <input
          value={ing.item}
          onChange={e => { onChange({ item: e.target.value }); setSuggestOpen(true); }}
          onFocus={() => setSuggestOpen(true)}
          onBlur={() => setTimeout(() => setSuggestOpen(false), 160)}
          placeholder="ingredient"
          style={{ ...inputStyle, flex: 1 }}
        />
        {onRemove && (
          <button onClick={onRemove} style={iconBtnSmall}>×</button>
        )}
      </div>

      {/* Pantry link indicator */}
      {hasLink && (
        <div style={{ marginTop: 6, fontFamily: FONT.mono, fontSize: 9, color: CHIP_TONES.canonical.fg, letterSpacing: "0.1em" }}>
          LINKED · {ing.ingredientId}
          <button
            onClick={() => onChange({ ingredientId: null })}
            style={{ marginLeft: 8, background: "transparent", border: "none", color: COLOR.muted, fontFamily: FONT.mono, fontSize: 9, cursor: "pointer" }}
          >
            UNLINK
          </button>
        </div>
      )}

      {/* Pantry suggestions — shown while the item text has a decent
          prefix and there's a match. Tapping attaches the canonicalId. */}
      {suggestOpen && matches.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          {matches.map((m, i) => (
            <button
              key={i}
              onMouseDown={e => e.preventDefault()}
              onClick={() => {
                onChange({ item: m.name, ingredientId: m.canonicalId || null });
                setSuggestOpen(false);
              }}
              style={{
                textAlign: "left", background: COLOR.soil, border: `1px solid ${COLOR.border}`,
                borderRadius: 8, padding: "6px 10px",
                fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim,
                cursor: "pointer",
              }}
            >
              {m.name}
              {m.canonicalId && (
                <span style={{ marginLeft: 8, fontFamily: FONT.mono, fontSize: 9, color: CHIP_TONES.canonical.fg }}>
                  · {m.canonicalId}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StepRow({ index, step, onChange, onRemove }) {
  return (
    <div style={{ background: COLOR.ground, border: `1px solid ${COLOR.edge}`, borderRadius: 12, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{
          width: 22, height: 22, borderRadius: 11,
          background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
          fontFamily: FONT.mono, fontSize: 11, color: COLOR.gold,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {index + 1}
        </span>
        <input
          value={step.title}
          onChange={e => onChange({ title: e.target.value })}
          placeholder="Short step title"
          style={{ ...inputStyle, flex: 1 }}
        />
        {onRemove && (
          <button onClick={onRemove} style={iconBtnSmall}>×</button>
        )}
      </div>
      <textarea
        value={step.instruction}
        onChange={e => onChange({ instruction: e.target.value })}
        placeholder="What happens in this step?"
        rows={3}
        style={{ ...inputStyle, resize: "vertical", width: "100%", boxSizing: "border-box" }}
      />
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em", marginBottom: 10 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ flex: 1, marginTop: 10 }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, letterSpacing: "0.12em", marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared style tokens

const inputStyle = {
  padding: "10px 12px",
  background: COLOR.deep,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10,
  color: COLOR.ink,
  fontFamily: FONT.sans,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  width: "100%",
};

const iconBtn = {
  background: COLOR.soil, border: `1px solid ${COLOR.border}`,
  borderRadius: 18, width: 34, height: 34,
  color: COLOR.dim, fontSize: 16, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1,
};
const iconBtnSmall = {
  background: COLOR.roseDeep, border: `1px solid ${COLOR.roseDim}`,
  borderRadius: 14, width: 26, height: 26,
  color: COLOR.rose, fontSize: 14, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1,
  flexShrink: 0,
};
const addBtn = {
  marginTop: 10, width: "100%", padding: "10px",
  background: "transparent", border: `1px dashed ${COLOR.border}`,
  color: COLOR.dim, borderRadius: 10,
  fontFamily: FONT.mono, fontSize: 10,
  letterSpacing: "0.1em", cursor: "pointer",
};

function uid() {
  return Math.random().toString(36).slice(2, 10);
}
function clamp(n, lo, hi) {
  const v = Number(n);
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}
function difficultyWord(n) {
  if (n <= 3) return "EASY";
  if (n <= 6) return "MEDIUM";
  return "ADVANCED";
}

// Bottom-sheet dropdown replacement — replaces the native `<select>`
// for the CUISINE / CATEGORY / COURSE / MEAL TIMING fields so the
// recipe builder matches the ModalSheet picker pattern the rest of
// the app uses for discrete identity axes (CLAUDE.md).
//
// Props:
//   label, kickerColor   — kicker line above the title ("CUISINE", etc.)
//   title                — the sheet's headline ("Pick a cuisine")
//   value, options       — current pick + option list
//   onChange(id)         — fired with the picked id
//   renderOption(opt)    — optional custom label renderer
function SelectButton({
  label, kickerColor = COLOR.gold,
  title, value, options,
  onChange, renderOption,
}) {
  const [open, setOpen] = useState(false);
  const active = options.find(o => {
    const id = typeof o === "string" ? o : o.id;
    return id === value;
  });
  const display = active
    ? (typeof active === "string" ? active : active.label)
    : "—";
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          ...inputStyle,
          textAlign: "left",
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span>{display}</span>
        <span style={{
          fontFamily: FONT.mono, fontSize: 10,
          color: COLOR.dim, letterSpacing: "0.1em",
        }}>▾</span>
      </button>
      {open && (
        <ModalSheet onClose={() => setOpen(false)} maxHeight="70vh">
          <div style={pickerKicker(kickerColor)}>{label}</div>
          <h2 style={pickerTitle}>{title}</h2>
          <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0", display: "flex", flexDirection: "column", gap: 6 }}>
            {options.map(o => {
              const id    = typeof o === "string" ? o : o.id;
              const lbl   = typeof o === "string" ? o : o.label;
              const isOn  = id === value;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => { onChange(id); setOpen(false); }}
                    style={{
                      ...pickerOptionStyle(isOn, CHIP_TONES.category),
                      width: "100%",
                      color: isOn ? CHIP_TONES.category.fg : COLOR.ink,
                      fontSize: 14,
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      {renderOption ? renderOption(o) : lbl}
                    </span>
                    {isOn && <span style={{ fontFamily: FONT.mono, fontSize: 12 }}>✓</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </ModalSheet>
      )}
    </>
  );
}
